import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { View } from 'react-native';
import { z } from 'zod';

import { ControlledOptionRows, FieldBlock } from '@/components/services/wizard/field';
import { SheetStepFooter, TeamSheet } from '@/components/team/team-sheet';
import { Input } from '@/components/ui/input';
import api from '@/services/api';
import { showToast } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';

const REASONS = [
  { value: 'SPAM', label: 'Spam ou propaganda', meaning: 'Conteúdo repetitivo ou enganoso.' },
  { value: 'ABUSIVE', label: 'Ofensivo ou assédio', meaning: 'Ofensa, ameaça ou discriminação.' },
  { value: 'FRAUD', label: 'Golpe ou fraude', meaning: 'Pede pagamento ou dados suspeitos.' },
  {
    value: 'INAPPROPRIATE',
    label: 'Conteúdo impróprio',
    meaning: 'Foto ou texto sexual ou violento.',
  },
  { value: 'OTHER', label: 'Outro motivo', meaning: 'Explique no campo abaixo.' },
] as const;

type Reason = (typeof REASONS)[number]['value'];

const schema = z
  .object({
    reason: z.enum(REASONS.map((r) => r.value) as [Reason, ...Reason[]], {
      message: 'Escolha o motivo.',
    }),
    details: z.string().max(1000, 'Até 1000 caracteres.').optional(),
  })
  .refine((v) => v.reason !== 'OTHER' || !!v.details?.trim(), {
    path: ['details'],
    message: 'Conte o que aconteceu.',
  });

type Form = z.input<typeof schema>;

/**
 * "Denunciar perfil" (App Store 1.2). Funciona sem login: quem chegou pela
 * busca ou por um link também precisa poder denunciar. Vai ao suporte, que
 * responde em até 24h.
 */
export function ReportProfileSheet({
  slug,
  open,
  onClose,
}: {
  slug: string;
  open: boolean;
  onClose: () => void;
}) {
  const { control, handleSubmit, reset, watch } = useForm<Form>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { reason: undefined, details: '' },
  });

  // "Outro motivo" só se entende com o texto: aí o campo deixa de ser opcional.
  const detailsRequired = watch('reason') === 'OTHER';

  useEffect(() => {
    if (open) reset({ reason: undefined, details: '' });
  }, [open, reset]);

  const report = useMutation({
    mutationFn: async (form: Form) =>
      api.post(`/moderation/profiles/${encodeURIComponent(slug)}/report`, {
        reason: form.reason,
        details: form.details?.trim() || undefined,
      }),
  });

  const onSubmit = (form: Form) =>
    report.mutate(form, {
      onSuccess: () => {
        showToast('Denúncia enviada. Nossa equipe analisa em até 24 horas.', 'success');
        onClose();
      },
      onError: (err) =>
        showToast(getApiErrorMessage(err, 'Não foi possível enviar a denúncia.'), 'error'),
    });

  return (
    <TeamSheet
      open={open}
      onClose={onClose}
      busy={report.isPending}
      title="Denunciar perfil"
      subtitle="Conte o que há de errado"
      footer={
        <SheetStepFooter
          onNext={handleSubmit(onSubmit)}
          nextLabel="Enviar denúncia"
          busy={report.isPending}
        />
      }
    >
      <View className="gap-5">
        <ControlledOptionRows
          control={control}
          name="reason"
          title="Motivo"
          options={[...REASONS]}
        />
        <Controller
          control={control}
          name="details"
          render={({ field, fieldState }) => (
            <FieldBlock
              title="Detalhes"
              note={detailsRequired ? 'Obrigatório' : 'Opcional'}
              error={fieldState.error?.message}
            >
              <Input
                value={field.value ?? ''}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                placeholder="O que você viu?"
                multiline
              />
            </FieldBlock>
          )}
        />
      </View>
    </TeamSheet>
  );
}
